import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { PlayerService } from "@/lib/services/player";
import { useListRolesQuery, RoleDto } from "@/store/apiSlice";
import { toast } from "sonner";

interface EditableUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  is_active: boolean;
  is_verified: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  role?: string | null;
}

export default function PlayerEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<EditableUser | null>(null);
  const [form, setForm] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });
  const [statusFlags, setStatusFlags] = useState({
    is_active: true,
    is_verified: false,
    is_superuser: false,
  });
  const [selectedRoleId, setSelectedRoleId] = useState<number | "">("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { data: rolesData } = useListRolesQuery();

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    setIsLoading(true);
    PlayerService.getPlayerById(id)
      .then((data) => {
        if (!mounted) return;
        const u = data as any;
        const editable: EditableUser = {
          id: u.id,
          username: u.username,
          email: u.email,
          first_name: u.first_name ?? "",
          last_name: u.last_name ?? "",
          phone_number: u.phone_number ?? "",
          is_active: Boolean(u.is_active),
          is_verified: Boolean(u.is_verified),
          is_staff: Boolean(u.is_staff),
          is_superuser: Boolean(u.is_superuser),
          role: u.role ?? null,
        };
        setUser(editable);
        setForm({
          username: editable.username,
          first_name: editable.first_name,
          last_name: editable.last_name,
          email: editable.email,
          phone_number: editable.phone_number ?? "",
        });
        setStatusFlags({
          is_active: editable.is_active,
          is_verified: editable.is_verified,
          is_superuser: editable.is_superuser,
        });
      })
      .catch((e) => {
        console.error(e);
        toast.error("Failed to load user.");
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  // Pre-select the current team in the dropdown when roles are loaded
  useEffect(() => {
    if (!user || !rolesData || selectedRoleId) return;
    const currentRoleName = (user.role || "").toString().toLowerCase().trim();
    if (!currentRoleName) return;
    const roles = rolesData as RoleDto[];
    const match = roles.find((r) => {
      const name = (r.name || "").toString().toLowerCase().trim();
      const code = (r.code || "").toString().toLowerCase().trim();
      return name === currentRoleName || code === currentRoleName;
    });
    if (match) {
      setSelectedRoleId(match.id);
    }
  }, [user, rolesData, selectedRoleId]);

  const handleSave = async () => {
    if (!id || !user) return;
    if (!form.email.trim()) {
      toast.error("Email is required.");
      return;
    }
    try {
      setIsSaving(true);
      await PlayerService.adminQuickUpdate(id, {
        username: form.username || undefined,
        first_name: form.first_name || undefined,
        last_name: form.last_name || undefined,
        email: form.email,
        ...statusFlags,
        team_id: selectedRoleId ? Number(selectedRoleId) : undefined,
      });
      toast.success("User updated.");
      navigate(`/players/${id}`);
    } catch (e: any) {
      console.error(e);
      const data = e?.response?.data;
      if (data && typeof data === "object") {
        const messages: string[] = [];
        Object.entries(data).forEach(([field, value]) => {
          if (Array.isArray(value)) {
            value.forEach((msg) => {
              messages.push(`${field}: ${String(msg)}`);
            });
          } else {
            messages.push(`${field}: ${String(value)}`);
          }
        });
        toast.error(messages.join("\n"));
      } else {
        toast.error("Failed to update user.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!id) {
    return (
      <div className="mx-auto px-4 py-8">
        <p className="text-muted-foreground">Invalid player.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-8 space-y-4">
      <Link to={`/players/${id}`}>
        <Button variant="ghost" className="mb-2">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to player
        </Button>
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Edit User</CardTitle>
          <CardDescription>Update profile details, team, and access status.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading || !user ? (
            <p className="text-sm text-muted-foreground">Loading user...</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Username</p>
                  <Input
                    value={form.username}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, username: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Email</p>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">First name</p>
                  <Input
                    value={form.first_name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, first_name: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Last name</p>
                  <Input
                    value={form.last_name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, last_name: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Team</p>
                <select
                  className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                  value={selectedRoleId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedRoleId(v ? Number(v) : "");
                  }}
                >
                  <option value="">Select team</option>
                  {(rolesData as RoleDto[] | undefined)?.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name || role.code}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">Active</span>
                  <Switch
                    checked={statusFlags.is_active}
                    onCheckedChange={(checked: boolean) =>
                      setStatusFlags((prev) => ({ ...prev, is_active: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">Verified</span>
                  <Switch
                    checked={statusFlags.is_verified}
                    onCheckedChange={(checked: boolean) =>
                      setStatusFlags((prev) => ({ ...prev, is_verified: checked }))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/players/${id}`)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving || !form.email.trim()}
                >
                  {isSaving ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
